import React from 'react';
import {
  Edit,
  SimpleForm,
  BooleanInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  FormDataConsumer,
  FileInput,
  FileField,
  useNotify,
  useInput,
  SaveButton,
  Toolbar,
  DeleteButton,
  useRefresh,
} from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { Grid, Typography, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Permission, RequiredChain } from '../../components';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import styled from '@emotion/styled';
import { TagType } from '../../models';
import frameIphone from '../../assets/images/frame-iphone-14.png';
import { track, EVENTS } from '../../lib/analytics';

const MAX_SIZE = 5000000; // 5MB

const WIDTH = 292;
const HEIGHT = 590;

const MobileWrapper = styled('div')({
  position: 'relative',
  width: WIDTH,
  margin: '20px auto',
});

const MobileContainer = styled('div')({
  width: WIDTH - 40,
  height: HEIGHT - 90,
  backgroundColor: '#f0f0f0',
  margin: '60px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  position: 'relative',
});

const MobileFrame = styled('img')({
  position: 'absolute',
  width: WIDTH,
  height: HEIGHT,
  top: -60,
  pointerEvents: 'none',
  zIndex: 9,
});

const PopupOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
});

const PopupContent = styled('div')({
  position: 'relative',
  borderRadius: 12,
  overflow: 'hidden',
  maxWidth: '100%',
  lineHeight: 0,
  backgroundColor: '#fff',
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
    verticalAlign: 'top',
    objectFit: 'contain',
  },
});

const CloseButtonStyled = styled(IconButton)({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 10,
  backgroundColor: '#ffffff',
  borderRadius: 20,
  padding: 4,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
});

const convertBitToMb = (value: number) => {
  return `${value / 1000000}MB`;
};

const validateDates = (value: any, allValues: any) => {
  if (allValues.startDate && allValues.endDate) {
    const start = new Date(allValues.startDate);
    const end = new Date(allValues.endDate);

    if (end < start) {
      return 'Data fim não pode ser anterior à data início';
    }
  }
  return undefined;
};

const validateMinInterval = (value: any, allValues: any) => {
  if (!allValues.showAlways && value !== undefined && value !== null) {
    if (value <= 0) {
      return 'Intervalo mínimo deve ser maior que zero';
    }
    if (!Number.isInteger(Number(value))) {
      return 'Intervalo mínimo deve ser um número inteiro';
    }
  }
  return undefined;
};

const validateRequiredIfNotShowAlways = (value: any, allValues: any) => {
  if (!allValues.showAlways && (value === undefined || value === null || value === '')) {
    return 'Campo obrigatório quando "Mostrar sempre" está desativado';
  }
  return undefined;
};

const validateImage = (value: any, allValues: any) => {
  if (allValues.imageUrl && !value) {
    return undefined;
  }

  if (!allValues.imageUrl && !value) {
    return 'Imagem é obrigatória';
  }

  if (value && value.rawFile) {
    const file = value.rawFile;

    if (file.size > MAX_SIZE) {
      return `Tamanho do arquivo excede ${convertBitToMb(MAX_SIZE)}`;
    }

    if (!file.type.includes('image')) {
      return 'Apenas imagens são permitidas';
    }
  }

  return undefined;
};

const validates = (values) => {
  const errors: any = {};

  const imageError = validateImage(values.image, values);
  if (imageError) {
    errors.image = imageError;
  }

  const startDateError = validateDates(values.startDate, values);
  if (startDateError) {
    errors.startDate = startDateError;
  }

  const endDateError = validateDates(values.endDate, values);
  if (endDateError) {
    errors.endDate = endDateError;
  }

  if (!values.showAlways) {
    const minIntervalRequiredError = validateRequiredIfNotShowAlways(values.minInterval, values);
    if (minIntervalRequiredError) {
      errors.minInterval = minIntervalRequiredError;
    } else {
      const minIntervalError = validateMinInterval(values.minInterval, values);
      if (minIntervalError) {
        errors.minInterval = minIntervalError;
      }
    }
  }
  return errors;
}

const PopUpEdit: React.FC = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const chainId = getChainId();

  const onEditSuccess = () => {
    track(EVENTS.POPUP_PUBLISHED, { chain_id: chainId ?? 'unknown' });
    notify('ra.notification.updated', { messageArgs: { smart_count: 1 } });
    navigate('/pop-ups');
  };

  const onDeleteSuccess = () => {
    notify('ra.notification.deleted', { messageArgs: { smart_count: 1 } });
    refresh();
    navigate('/pop-ups');
  };

  const onError = () => {
    notify('ra.notification.data_provider_error', { type: 'error' });
  };

  const ImagePreview: React.FC<{ formData: any }> = ({ formData }) => {
    const fileInputField = useInput({ source: 'image' });
    const file = fileInputField.field.value;

    const hasImage = (file && file.src) || formData?.imageUrl;
    const imageSrc = file?.src || formData?.imageUrl;

    return (
      <MobileWrapper>
        <MobileContainer>
          {hasImage ? (
            <PopupOverlay>
              <PopupContent>
                <CloseButtonStyled size="small">
                  <CloseIcon fontSize="small" sx={{ color: 'black' }} />
                </CloseButtonStyled>
                <img src={imageSrc} alt="Preview do PopUp" />
              </PopupContent>
            </PopupOverlay>
          ) : (
            <Typography color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              Nenhuma imagem selecionada
            </Typography>
          )}
        </MobileContainer>
        <MobileFrame src={frameIphone} draggable={false} />
      </MobileWrapper>
    );
  };

  const ToolbarCustom = (props: any) => (
    <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton mutationOptions={{ onSuccess: onDeleteSuccess, onError }} />
    </Toolbar>
  );

  return (
    <RequiredChain>
      <Permission permission={PERMISSIONS.POP_UPS}>
        <Edit
          id={getChainId()}
          resource="pop-ups"
          title="Editar Pop-up"
          mutationMode="pessimistic"
          mutationOptions={{ onSuccess: onEditSuccess, onError }}
          transform={(data: any) => {
            const transformed = { ...data };

            if (transformed.startDate) {
              transformed.startDate = new Date(transformed.startDate).toISOString();
            }
            if (transformed.endDate) {
              transformed.endDate = new Date(transformed.endDate).toISOString();
            }

            if (transformed.showAlways) {
              delete transformed.minInterval;
            }

            return transformed;
          }}
        >
          <SimpleForm toolbar={<ToolbarCustom />} validate={validates}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Informações do Pop-up
                </Typography>
                <FileInput
                  source="image"
                  label="Imagem"
                  accept="image/*"
                  maxSize={MAX_SIZE}
                >
                  <FileField source="src" title="title" />
                </FileInput>
                <Typography sx={{ marginBottom: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                  {`Permitido imagens de até ${convertBitToMb(MAX_SIZE)}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <DateTimeInputUTC
                    source="startDate"
                    label="Data Início"
                  />
                  <DateTimeInputUTC
                    source="endDate"
                    label="Data Fim"
                  />
                </Box>
                <BooleanInput
                  source="showAlways"
                  label="Mostrar sempre"
                />
                <FormDataConsumer>
                  {({ formData }) => (
                    !formData.showAlways && (
                      <Box sx={{ width: '100%', mb: 2 }}>
                        <NumberInput
                          source="minInterval"
                          label="Intervalo Mínimo (em Horas)"
                          sx={{ maxWidth: 250 }}
                          format={(val: number) => (val !== undefined && val !== null) ? Math.floor(val) : ''}
                          parse={(val: string) => {
                            const parsed = parseInt(val, 10);
                            return isNaN(parsed) ? null : parsed;
                          }}
                          helperText="Horas entre exibições"
                        />
                      </Box>
                    )
                  )}
                </FormDataConsumer>
                <Box sx={{ width: '100%' }}>
                  <ReferenceInput
                    source="tagId"
                    reference="chains/chainId/tag"
                    filter={{ type: TagType.client }}
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <SelectInput
                      optionText="name"
                      label="Etiqueta do cliente"
                      emptyText="Nenhuma etiqueta selecionada"
                      sx={{ minWidth: 220 }}
                    />
                  </ReferenceInput>
                </Box>
                <BooleanInput
                  source="isActive"
                  label="Pop-up Ativo"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormDataConsumer>
                  {({ formData }) => <ImagePreview formData={formData} />}
                </FormDataConsumer>
              </Grid>
            </Grid>
          </SimpleForm>
        </Edit>
      </Permission>
    </RequiredChain >
  );
};

export default PopUpEdit;
